'use strict';


import { FullScreenQuad, Pass } from 'three/examples/jsm/postprocessing/Pass';
import { Color, LinearSRGBColorSpace, NoBlending, ShaderMaterial, UniformsUtils, WebGLRenderTarget } from 'three';
import { SSAODepthShader } from 'three/examples/jsm/shaders/SSAOShader';
import { CopyShader } from 'three/examples/jsm/shaders/CopyShader';
import vertex from './Shaders/Fog/vertex.glsl';
import fragment from './Shaders/Fog/fragment.glsl';
import { Effect } from './Effect';
import { threeJsVariables } from '../../../settings';

class Fog extends Pass{
	constructor (camera,width,height) {
		super();
		
		this.camera = camera;
		this.width = width;
		this.height = height;
		
		this.depthRenderMaterial = new ShaderMaterial( {
			defines: Object.assign( {}, SSAODepthShader.defines ),
			uniforms: UniformsUtils.clone( SSAODepthShader.uniforms ),
			vertexShader: SSAODepthShader.vertexShader,
			fragmentShader: SSAODepthShader.fragmentShader,
			blending: NoBlending
		} );
		
		this.depthRenderMaterial.uniforms.cameraNear.value = this.camera.near;
		this.depthRenderMaterial.uniforms.cameraFar.value = this.camera.far;
		
		this.defaultMaterial = new ShaderMaterial({
			uniforms: UniformsUtils.clone( CopyShader.uniforms ),
			vertexShader: CopyShader.vertexShader,
			fragmentShader: CopyShader.fragmentShader,
			transparent: true,
			depthTest: false,
			depthWrite: false,
		});
		
		this.fogMaterial = new ShaderMaterial({
			uniforms: {
				tDiffuse: {value: null},
				tDepth: {value: null},
				fogColor: {value: new Color()},
				fogDensity: {value: 0.88},
				fogNear: {value: 0.95},
				fogFar: {value: 0.83}
			},
			vertexShader: vertex,
			fragmentShader: fragment,
			transparent: true,
			depthWrite: false,
			depthTest: false
		});
		
		this.uniforms = this.fogMaterial.uniforms;
		
		this.defaultRt = new WebGLRenderTarget(this.width,this.height);
		this.depthRt = new WebGLRenderTarget(this.width,this.height);
		this.fsQuad = new FullScreenQuad(null);
		this.originalClearColor = new Color();
		this.needsSwap = false;
	}
	
	setSize( width, height) {
		this.width = width;
		this.height = height;
		this.defaultRt.setSize(width,height);
		this.depthRt.setSize(width,height);
	}
	
	render( renderer, writeBuffer, readBuffer) {
		//Render beauty
		this.defaultMaterial.uniforms.tDiffuse.value = readBuffer.texture;
		this.renderPass(renderer,this.defaultMaterial,this.defaultRt);
		// Depth
		this.depthRenderMaterial.uniforms.tDepth.value = readBuffer.depthTexture;
		this.renderPass(renderer,this.depthRenderMaterial,this.depthRt);
		
		this.fogMaterial.uniforms[ 'tDiffuse' ].value = this.defaultRt.texture;
		this.fogMaterial.uniforms[ 'tDepth' ].value = this.depthRt.texture;
		
		this.renderPass( renderer, this.fogMaterial, this.renderToScreen ? null : readBuffer );
	}
	
	renderPass( renderer, passMaterial, renderTarget, clearColor, clearAlpha ) {
		// save original state
		renderer.getClearColor( this.originalClearColor );
		const originalClearAlpha = renderer.getClearAlpha();
		const originalAutoClear = renderer.autoClear;
		
		renderer.setRenderTarget( renderTarget );
		
		// setup pass state
		renderer.autoClear = false;
		if ( ( clearColor !== undefined ) && ( clearColor !== null ) ) {
			
			renderer.setClearColor( clearColor );
			renderer.setClearAlpha( clearAlpha || 0.0 );
			renderer.clear();
			
		}
		
		this.fsQuad.material = passMaterial;
		this.fsQuad.render( renderer );
		
		// restore original state
		renderer.autoClear = originalAutoClear;
		renderer.setClearColor( this.originalClearColor );
		renderer.setClearAlpha( originalClearAlpha );
		
	}
	
}

export class FogEffect extends Effect{
	constructor (camera,width,height,params) {
		super();
		
		this.effect = new Fog(camera,width,height);
		this.defaultParams = params;
		this.params = {};
		this.active = false;
		this.setConfig(params);
	}
	
	setColor(color) {
		if (typeof color === 'string') {
			this.effect.uniforms.fogColor.value.setStyle(color,LinearSRGBColorSpace);
		} else {
			this.effect.uniforms.fogColor.value.copy(color);
		}
	}
	
	setDensity(density) {
		this.effect.uniforms.fogDensity.value = density;
	}
	
	setNear(value) {
		this.effect.uniforms.fogNear.value = value;
	}
	
	setFar(value) {
		this.effect.uniforms.fogFar.value = value;
	}
	
	setSky(sky) {
		if (sky) {
			this.effect.sky = sky;
			this.effect.fogMaterial.defines.USE_SKY = 1;
		} else {
			this.sky = null;
			this.effect.fogMaterial.defines.USE_SKY = 0;
		}
		this.effect.fogMaterial.needsUpdate = true;
	}
	
	getConfig() {
		return {
			active: this.active,
			fogColor: `#${this.effect.uniforms.fogColor.value.getHexString(LinearSRGBColorSpace)}`,
			fogNear: this.effect.uniforms.fogNear.value,
			fogFar: this.effect.uniforms.fogFar.value,
			fogDensity: this.effect.uniforms.fogDensity.value
		};
	}
	
	setConfig(config) {
		Object.entries(config).forEach(([key,value]) => {
			if (key === 'active') {
				this.active = value;
			} else {
				switch (key) {
					case 'fogColor':
						this.setColor(value);
						break;
					case 'fogNear':
						this.setNear(value);
						break;
					case 'fogFar':
						this.setFar(value);
						break;
					case 'fogDensity':
						this.setDensity(value);
						break;
				}
			}
		});
	}
	
	get preset() {
		return {
			active: this.active,
			fogColor: `#${this.effect.uniforms.fogColor.value.getHexString(LinearSRGBColorSpace)}`,
			fogNear: this.effect.uniforms.fogNear.value,
			fogFar: this.effect.uniforms.fogFar.value,
			fogDensity: this.effect.uniforms.fogDensity.value,
			// sky: this.effect.sky
		}
	}
	
	set preset(params) {
		this.setConfig(params);
		// this.effect.sky = params.sky;
	}
}


export function setFogParams (params) {
	const fogPass = threeJsVariables.composer.getPass('fog');
	if (fogPass) {
		fogPass.setConfig(params);
	}
}

export function restoreFogParams () {
	const fogPass = threeJsVariables.composer.getPass('fog');
	if (fogPass) {
		fogPass.preset = fogPass.params;
	}
}

export function saveFogPreset () {
	const fogPass = threeJsVariables.composer.getPass('fog');
	if (fogPass) {
		fogPass.params = fogPass.preset;
	}
}

export function resetFogToDefault () {
	const fogPass = threeJsVariables.composer.getPass('fog');
	if (fogPass) {
		fogPass.setConfig(fogPass.defaultParams);
	}
}
